// ─────────────────────────────────────────────────────────────────────────────
// Outbox Service - TypeScript Implementation
// Manages outbox pattern for eventual consistency and offline sync
// ─────────────────────────────────────────────────────────────────────────────

import { BACKUPS_PER_SEDATION } from "../utils/backupRetention";
import { getDb } from "../hooks/repos/dbrepo";
import { newGuid, nowIsoUtc } from "../utilis/baseEntity";
import { OutboxRecord, SedationCompletionPayload, EmailOutboxPayload } from "../types";
import { CompleteSedationBackup } from "../types/backupTypes";
import { createCleanBackupPayload } from "../utils/backupUrlIntegration";
import { productionLogger } from "../services/productionLoggingService";

// Outbox operation types
export type OutboxOperation = "create" | "update" | "upsert" | "delete" | "sedation_completed" | "email_send";

// Generic outbox enqueue parameters with type safety
export interface EnqueueOutboxParams<TData = any> {
  entity: string; // e.g., 'Patient', 'SedationDetail', 'SedationCompletion'
  entityId: string;
  op: OutboxOperation;
  payload: TData; // Strongly typed payload
}

// Generic enqueue function with strong typing
export async function enqueueOutbox<TData>({
  entity,
  entityId,
  op,
  payload,
}: EnqueueOutboxParams<TData>): Promise<string> {
  const db = await getDb();

  const record: OutboxRecord<TData> = {
    id: newGuid(),
    entityType: entity,
    entityId,
    operation: op as "create" | "update" | "delete" | "sedation_completed",
    data: payload,
    enqueuedAt: nowIsoUtc(),
    attempts: 0,
  };

  console.log('📥 Adding record to outbox:', {
    recordId: record.id,
    entity,
    entityId,
    operation: op,
    enqueuedAt: record.enqueuedAt,
    payloadSize: JSON.stringify(payload).length
  });

  // Log to production logger for remote monitoring
  productionLogger.info('Outbox record enqueued', {
    context: 'outbox_enqueue',
    operation: op,
    entityType: entity,
    entityId,
    recordId: record.id,
    payloadSize: JSON.stringify(payload).length,
    enqueuedAt: record.enqueuedAt
  });

  const tx = db.transaction(["outbox"], "readwrite");
  const store = tx.objectStore("outbox");
  await store.put(record);

  // Each sedation-completion event carries a full copy of the sedation. Keep only the newest
  // few per sedation — a newer one supersedes the older ones — so failed or repeated syncs
  // can't fill browser storage.
  if (op === "sedation_completed") {
    const sameSedation = ((await store.getAll()) as OutboxRecord[])
      .filter(r => r.operation === "sedation_completed" && r.entityId === entityId)
      .sort((a, b) => b.enqueuedAt.localeCompare(a.enqueuedAt));
    for (const stale of sameSedation.slice(BACKUPS_PER_SEDATION)) await store.delete(stale.id);
  }
  await tx.done;

  console.log('✅ Outbox record added successfully:', record.id);
  return record.id;
}

// Peek at outbox records for processing (PRIORITY ORDER: sedation completion first, then emails)
export async function peekOutbox(limit: number = 50): Promise<OutboxRecord[]> {
  const db = await getDb();
  const records: OutboxRecord[] = [];

  try {
    let cursor = await db
      .transaction("outbox")
      .store.index("byEnqueuedAt")
      .openCursor();
    while (cursor && records.length < 1000) { // Get more records to sort properly
      records.push(cursor.value);
      cursor = await cursor.continue();
    }
  } catch (error) {
    console.error("Error peeking outbox:", error);
    throw new Error("Failed to retrieve outbox records");
  }

  // PRIORITY SORTING: Process sedation completion events BEFORE email events
  const prioritizedRecords = records.sort((a, b) => {
    // Priority 1: Sedation completion events (highest priority)
    const aIsSedation = a.operation === 'sedation_completed' || a.entityType === 'SedationCompletion';
    const bIsSedation = b.operation === 'sedation_completed' || b.entityType === 'SedationCompletion';
    
    // Priority 2: Email events (lower priority)
    const aIsEmail = a.operation === 'email_send' || a.entityType === 'Email';
    const bIsEmail = b.operation === 'email_send' || b.entityType === 'Email';

    // If one is sedation and the other is not, sedation goes first
    if (aIsSedation && !bIsSedation) return -1;
    if (!aIsSedation && bIsSedation) return 1;

    // If one is email and the other is not (and neither is sedation), email goes first
    if (aIsEmail && !bIsEmail && !aIsSedation && !bIsSedation) return -1;
    if (!aIsEmail && bIsEmail && !aIsSedation && !bIsSedation) return 1;

    // Within the same priority level, sort by timestamp (oldest first)
    return new Date(a.enqueuedAt).getTime() - new Date(b.enqueuedAt).getTime();
  });

  // Return only the requested limit
  return prioritizedRecords.slice(0, limit);
}

// Remove a successfully processed record from outbox
export async function removeFromOutbox(id: string): Promise<void> {
  if (!id) {
    throw new Error("Outbox record ID is required");
  }

  console.log('🗑️ Removing processed record from outbox:', id);

  const db = await getDb();
  const tx = db.transaction("outbox", "readwrite");

  try {
    // Get record details before deletion for logging
    const record = (await tx.store.get(id)) as OutboxRecord | undefined;
    
    await tx.store.delete(id);
    await tx.done;

    console.log('✅ Outbox record removed successfully:', id);
    
    // Log successful removal to production logger
    productionLogger.info('Outbox record processed and removed', {
      context: 'outbox_remove',
      recordId: id,
      entityType: record?.entityType,
      entityId: record?.entityId,
      operation: record?.operation,
      attempts: record?.attempts,
      enqueuedAt: record?.enqueuedAt
    });
  } catch (error) {
    console.error(`❌ Error removing outbox record ${id}:`, error);
    
    // Log removal error to production logger
    productionLogger.error(`Failed to remove outbox record: ${error instanceof Error ? error.message : 'Unknown error'}`, {
      context: 'outbox_remove_error',
      recordId: id,
      errorDetails: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error
    });
    
    throw new Error("Failed to remove outbox record");
  }
}

// Increment attempt counter for failed sync
export async function bumpAttempt(id: string): Promise<void> {
  if (!id) {
    throw new Error("Outbox record ID is required");
  }

  console.log('🔄 Bumping attempt count for outbox record:', id);

  const db = await getDb();
  const tx = db.transaction("outbox", "readwrite");

  try {
    const record = (await tx.store.get(id)) as OutboxRecord | undefined;
    if (record) {
      const previousAttempts = record.attempts || 0;
      record.attempts = previousAttempts + 1;
      record.lastAttemptAt = nowIsoUtc();

      console.log(`📊 Attempt count increased: ${previousAttempts} → ${record.attempts} for ${record.entityType}:${record.entityId}`);

      // Add error information if attempts are getting high
      if (record.attempts > 3) {
        record.errors = record.errors || [];
        record.errors.push(
          `Attempt ${record.attempts} at ${record.lastAttemptAt}`
        );
        
        console.warn(`⚠️ High attempt count (${record.attempts}) for outbox record:`, {
          recordId: id,
          entityType: record.entityType,
          entityId: record.entityId,
          operation: record.operation
        });

        // Log high attempt count to production logger
        productionLogger.warn(`Outbox record has ${record.attempts} failed attempts`, {
          context: 'outbox_high_attempts',
          recordId: id,
          entityType: record.entityType,
          entityId: record.entityId,
          operation: record.operation,
          attempts: record.attempts,
          enqueuedAt: record.enqueuedAt,
          lastAttemptAt: record.lastAttemptAt
        });
      }

      await tx.store.put(record);

      // Log attempt bump to production logger
      productionLogger.info('Outbox record attempt incremented', {
        context: 'outbox_attempt_bump',
        recordId: id,
        entityType: record.entityType,
        entityId: record.entityId,
        operation: record.operation,
        attempts: record.attempts,
        lastAttemptAt: record.lastAttemptAt
      });
   
    } else {
      console.warn(`⚠️ Outbox record ${id} not found when attempting to bump attempts`);
      
      // Log missing record to production logger
      productionLogger.warn('Attempted to bump attempts for non-existent outbox record', {
        context: 'outbox_record_not_found',
        recordId: id
      });
    }
  } catch (error) {
    console.error(`❌ Error bumping attempt for outbox record ${id}:`, error);
    
    // Log bump error to production logger
    productionLogger.error(`Failed to bump attempt count: ${error instanceof Error ? error.message : 'Unknown error'}`, {
      context: 'outbox_bump_error',
      recordId: id,
      errorDetails: error instanceof Error ? {
        name: error.name,
        message: error.message,
        stack: error.stack
      } : error
    });
    
    throw new Error("Failed to update outbox record attempt count");
  }

  await tx.done;
}

// Get outbox statistics
export async function getOutboxStats(): Promise<{
  total: number;
  pending: number;
  failed: number;
  oldestRecord?: OutboxRecord;
}> {
  const allRecords = await peekOutbox(1000); // Get more records for accurate stats

  const stats = {
    total: allRecords.length,
    pending: allRecords.filter((r) => r.attempts === 0).length,
    failed: allRecords.filter((r) => r.attempts > 3).length,
    oldestRecord: allRecords.length > 0 ? allRecords[0] : undefined,
  };

  return stats;
}

// Clear all outbox records (use with caution)
export async function clearOutbox(): Promise<number> {
  const db = await getDb();
  const tx = db.transaction("outbox", "readwrite");

  try {
    const allRecords = await peekOutbox(1000);
    const count = allRecords.length;

    await tx.store.clear();
    await tx.done;

    return count;
  } catch (error) {
    console.error("Error clearing outbox:", error);
    throw new Error("Failed to clear outbox");
  }
}

// Get records that have failed multiple times
export async function getFailedRecords(
  minAttempts: number = 3
): Promise<OutboxRecord[]> {
  const allRecords = await peekOutbox(1000);
  return allRecords.filter((record) => record.attempts >= minAttempts);
}

// Retry a specific failed record by resetting its attempt count
export async function retryFailedRecord(id: string): Promise<void> {
  const db = await getDb();
  const tx = db.transaction("outbox", "readwrite");

  try {
    const record = (await tx.store.get(id)) as OutboxRecord | undefined;
    if (record) {
      record.attempts = 0;
      record.lastAttemptAt = undefined;
      record.errors = [];

      await tx.store.put(record);
    }
  } catch (error) {
    console.error(`Error retrying outbox record ${id}:`, error);
    throw new Error("Failed to retry outbox record");
  }

  await tx.done;
}

// Reset all records that have exhausted their retry budget so they get another chance on reconnect
export async function resetAllFailedRecords(maxAttempts: number = 10): Promise<number> {
  const allRecords = await peekOutbox(1000);
  const failed = allRecords.filter(r => r.attempts >= maxAttempts);
  for (const record of failed) {
    await retryFailedRecord(record.id);
  }
  if (failed.length > 0) {
    productionLogger.info(`Reset ${failed.length} permanently-failed outbox records for retry`, {
      context: 'outbox_reset_failed_records',
      count: failed.length
    });
  }
  return failed.length;
}

// Strongly typed sedation completion event enqueue function
export async function enqueueSedationCompletion(
  patientId: string,
  sedationId: string,
  completeBackupData: CompleteSedationBackup
): Promise<string> {
  console.log('📝 Adding sedation completion event to outbox...', { patientId, sedationId });

  // Log the backup data structure for debugging
  console.log('📊 Backup data structure:', {
    hasPatient: !!completeBackupData.Patient,
    hasSedationDetail: !!completeBackupData.SedationDetail,
    hasSedationSession: !!completeBackupData.IntraOppState?.SessionData,
    hasMeasurements: !!completeBackupData.SedationMeasurements?.length,
    measurementCount: completeBackupData.SedationMeasurements?.length || 0,
    hasReport: !!completeBackupData.SedationReportReceipt,
    hasBackupMetadata: !!completeBackupData.BackupMetadata
  });

  // Log to production logger with detailed backup structure
  productionLogger.info('Starting sedation completion enqueue process', {
    context: 'sedation_completion_enqueue',
    patientId,
    sedationId,
    backupDataStructure: {
      hasPatient: !!completeBackupData.Patient,
      hasSedationDetail: !!completeBackupData.SedationDetail,
      hasSedationSession: !!completeBackupData.IntraOppState?.SessionData,
      measurementCount: completeBackupData.SedationMeasurements?.length || 0,
      hasReport: !!completeBackupData.SedationReportReceipt,
      hasBackupMetadata: !!completeBackupData.BackupMetadata
    }
  });

  // CRITICAL FIX: Store RAW backup data in outbox (with blobs)
  // When offline processing occurs, we'll upload blobs to storage first
  // This ensures IDENTICAL behavior between online and offline paths
  console.log('🔄 Storing RAW backup data in outbox for offline processing with blob uploads');

  const payload: SedationCompletionPayload = {
    eventType: 'SEDATION_COMPLETED',
    patientId,
    sedationId,
    timestamp: new Date().toISOString(),
    data: completeBackupData, // Store RAW data with blobs for offline processing
    metadata: {
      source: 'electron_sedation_app',
      version: '1.0',
      aggregatedAt: new Date().toISOString()
    }
  };

  console.log('📦 Final payload structure:', {
    eventType: payload.eventType,
    payloadSize: JSON.stringify(payload).length,
    hasData: !!payload.data,
    timestamp: payload.timestamp
  });

  // Log payload creation to production logger
  productionLogger.info('Sedation completion payload created (RAW data for offline processing)', {
    context: 'sedation_completion_payload',
    patientId,
    sedationId,
    eventType: payload.eventType,
    payloadSize: JSON.stringify(payload).length,
    dataStructure: {
      hasPatient: !!completeBackupData.Patient,
      hasSedationDetail: !!completeBackupData.SedationDetail,
      hasSedationSession: !!completeBackupData.IntraOppState?.SessionData,
      measurementCount: completeBackupData.SedationMeasurements?.length || 0,
      hasReport: !!completeBackupData.SedationReportReceipt,
      hasBackupMetadata: !!completeBackupData.BackupMetadata
    }
  });

  const eventId = await enqueueOutbox<SedationCompletionPayload>({
    entity: 'SedationCompletion',
    entityId: sedationId,
    op: 'sedation_completed',
    payload
  });

  console.log('✅ Sedation completion event added to outbox with clean URLs:', eventId);
  
  // Log successful enqueue to production logger
  productionLogger.info('Sedation completion event successfully enqueued', {
    context: 'sedation_completion_success',
    patientId,
    sedationId,
    outboxRecordId: eventId,
    finalPayloadSize: JSON.stringify(payload).length
  });
  
  return eventId;
}

// Get sedation completion sync status
export async function getSedationSyncStatus(): Promise<{
  pendingSedationEvents: number;
  lastSedationSync: string | null;
}> {
  const allRecords = await peekOutbox(1000);
  
  const sedationEvents = allRecords.filter(record => 
    record.operation === 'sedation_completed' || 
    record.entityType === 'SedationCompletion'
  );

  const lastSedationEvent = sedationEvents
    .sort((a, b) => new Date(b.enqueuedAt).getTime() - new Date(a.enqueuedAt).getTime())[0];

  return {
    pendingSedationEvents: sedationEvents.length,
    lastSedationSync: lastSedationEvent?.enqueuedAt || null
  };
}

// Enqueue email for later sending when device comes online
export async function enqueueEmailForSending(
  emailMessage: EmailOutboxPayload['emailMessage'],
  patientId: string,
  sedationId: string
): Promise<string> {
  console.log('📧 Adding email to outbox for later sending...', { 
    patientId, 
    sedationId,
    recipients: emailMessage.To.length 
  });

  // Create email payload for outbox
  const payload: EmailOutboxPayload = {
    emailMessage,
    patientId,
    sedationId,
    retryCount: 0
  };

  // Log to production logger
  productionLogger.info('Email enqueued for offline sending', {
    context: 'email_outbox_enqueue',
    patientId,
    sedationId,
    recipientCount: emailMessage.To.length,
    subject: emailMessage.Subject,
    hasAttachments: !!(emailMessage.Attachments?.length),
    attachmentCount: emailMessage.Attachments?.length || 0
  });

  const eventId = await enqueueOutbox<EmailOutboxPayload>({
    entity: 'Email',
    entityId: `${patientId}_${sedationId}_${Date.now()}`,
    op: 'email_send',
    payload
  });

  console.log('✅ Email added to outbox for retry when online:', eventId);
  
  // Log successful email enqueue to production logger
  productionLogger.info('Email successfully enqueued for retry', {
    context: 'email_outbox_success',
    patientId,
    sedationId,
    outboxRecordId: eventId,
    payloadSize: JSON.stringify(payload).length
  });

  return eventId;
}

// Get email sync status
export async function getEmailSyncStatus(): Promise<{
  pendingEmails: number;
  lastEmailSync: string | null;
}> {
  const allRecords = await peekOutbox(1000);
  
  const emailEvents = allRecords.filter(record => 
    record.operation === 'email_send' || 
    record.entityType === 'Email'
  );

  const lastEmailEvent = emailEvents
    .sort((a, b) => new Date(b.enqueuedAt).getTime() - new Date(a.enqueuedAt).getTime())[0];

  return {
    pendingEmails: emailEvents.length,
    lastEmailSync: lastEmailEvent?.enqueuedAt || null
  };
}
